import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

export default function AdminReview() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | success | error
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState("error");
        setErr("Missing review token.");
        return;
      }
      try {
        const { data } = await api.post("/admin/review-development", { token });
        if (cancelled) return;
        setResult(data);
        setState("success");
      } catch (e) {
        if (cancelled) return;
        setState("error");
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const approved = result?.status === "approved";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-6 sm:px-12 py-6 border-b border-neutral-100">
        <Logo />
      </header>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center" data-testid="admin-review-card">
          {state === "loading" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-propera-light text-propera flex items-center justify-center mb-6">
                <Loader2 size={26} className="animate-spin" />
              </div>
              <h1 className="text-3xl font-bold text-neutral-900">Processing review…</h1>
            </>
          )}
          {state === "success" && (
            <>
              <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6 ${approved ? "bg-propera-light text-propera" : "bg-amber-50 text-amber-600"}`}>
                <CheckCircle2 size={26} />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-2 text-neutral-500">{approved ? "Approved" : "Changes Requested"}</p>
              <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900" data-testid="admin-review-result">
                {result?.development?.name || "Development"}
              </h1>
              <p className="text-neutral-500 mt-3">
                {approved
                  ? "This development is now live on Propera and visible to investors. The developer has been notified."
                  : "We've notified the developer that changes are required before publishing."}
              </p>
              <Link to="/discover" data-testid="admin-review-discover" className="mt-8 inline-flex items-center justify-center gap-2 bg-propera hover:bg-propera-hover text-white rounded-full px-6 py-3 font-semibold transition">
                Open Propera
              </Link>
            </>
          )}
          {state === "error" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mb-6">
                <AlertCircle size={26} />
              </div>
              <h1 className="text-3xl font-bold text-neutral-900">Review link invalid</h1>
              <p className="text-neutral-500 mt-2" data-testid="admin-review-error">{err}</p>
              <Link to="/" className="mt-6 inline-block text-sm text-propera font-semibold hover:underline">Go home</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
